
import { Dispatch, AnyAction } from 'redux'

import { requestBegin, requestFail, requestSuccess, resetApiOperationState } from './actions'
import { FirstReturn } from './typeHelpers'

export type CreateApiOperationOptions = {
  onComplete?: (responseData: any, dispatch: Dispatch) => void,
  onFail?: (responseData: any, dispatch: Dispatch) => void
}
export type Operation<TData> = (dispatch: Dispatch<AnyAction>) => Promise<TData | null>
export const resetRequestState = (requestName: string) => resetApiOperationState(requestName)
export type Method = 'GET' | 'POST' | 'PUT' | 'DELETE' | 'HEAD' | 'OPTIONS' | 'PATCH'
export type FetcherParams<TParams, TOptions> = { method: Method, url: string, data?: TParams, options?: TOptions }
export type Fetcher<TParams, TOptions, TData> = (params: FetcherParams<TParams, TOptions>) => Promise<TData>
type ParamsBuilder<TParams> = (params: TParams) => FetcherParams<any, any>

export const createApiOperationCreator = (fetcher: Fetcher<any, any, any>) => <TParams, TData>(paramsBuilder: ParamsBuilder<TParams>, options: CreateApiOperationOptions = {}) => (requestName: string) => (params: TParams): Operation<TData> => async (dispatch: Dispatch): Promise<TData | null> => {
  const { onComplete, onFail } = options
  dispatch(requestBegin(requestName))
  let responseData

  try {
    responseData = await fetcher(paramsBuilder(params))
  } catch (responseErrorData) {
    if (onFail) {
      onFail(responseErrorData, dispatch)
    }
    dispatch(requestFail(requestName, responseErrorData))
    throw responseErrorData
  }

  if (onComplete) {
    onComplete(responseData, dispatch)
  }
  dispatch(requestSuccess(requestName, responseData))

  return responseData
}

export type CompiledApiOperations<TRawOperations> = {
  [K in keyof TRawOperations]: FirstReturn<TRawOperations[K]>
}
export const compileApiOperations = <T>(rawApiOperations: T): CompiledApiOperations<T> => {
  const apiOperationNames = Object.keys(rawApiOperations) as (keyof typeof rawApiOperations)[]
  const compiledApiOperations = apiOperationNames.reduce((acc: any, apiOperationName: keyof T) => {
    acc[apiOperationName] = (rawApiOperations as any)[apiOperationName](apiOperationName)
    return acc
  }, {})
  return compiledApiOperations
}
